// From an imported outline to a Site the engines can use.
//
// Two things a cadastral outline needs before it is a plot:
//
// - **Fewer vertices.** A surveyed boundary carries every fence post, so one
//   straight side is often five points a few centimetres off line. Each
//   would become its own edge with its own setback and its own candidate
//   wing. Points within `tolerance` of the line through their neighbours go.
// - **A local origin.** Stereo 70 values are around 500,000 m; the engines
//   are fine with that, the 3D view is not (float32 jitter). The ring is
//   moved next to the origin and the offset kept, so anything exported can
//   be put back where it came from.
import { normalise, ringArea, type Pt, type Ring } from "./geom.ts";
import type { EdgeKind, Site } from "./rules.ts";

export interface PreparedPlot {
  ring: Ring; // local metres, counter-clockwise
  origin: Pt; // add back to get Stereo 70 (east, north)
  area: number;
  removed: number; // vertices dropped as collinear or duplicate
  warnings: string[];
}

function distanceToLine(p: Pt, a: Pt, b: Pt): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  return Math.abs(dx * (a[1] - p[1]) - dy * (a[0] - p[0])) / len;
}

/** Drop duplicates and vertices that lie (nearly) on the line between their neighbours. */
export function simplify(ring: Ring, tolerance = 0.05): Ring {
  let r = ring.filter((p, i) => {
    const q = ring[(i + 1) % ring.length];
    return Math.hypot(p[0] - q[0], p[1] - q[1]) > 1e-3;
  });
  for (let changed = true; changed && r.length > 3;) {
    changed = false;
    for (let i = 0; i < r.length && r.length > 3; i++) {
      const prev = r[(i - 1 + r.length) % r.length], next = r[(i + 1) % r.length];
      if (distanceToLine(r[i], prev, next) <= tolerance) { r = r.filter((_, k) => k !== i); changed = true; break; }
    }
  }
  return r;
}

function selfIntersects(r: Ring): boolean {
  const n = r.length;
  const cross = (a: Pt, b: Pt, c: Pt) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
    if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1)) continue;
    const [a, b, c, d] = [r[i], r[(i + 1) % n], r[j], r[(j + 1) % n]];
    if (cross(a, b, c) * cross(a, b, d) < 0 && cross(c, d, a) * cross(c, d, b) < 0) return true;
  }
  return false;
}

export function preparePlot(raw: Ring, tolerance = 0.05): PreparedPlot {
  const warnings: string[] = [];
  const simple = normalise(simplify(raw, tolerance));
  const xs = simple.map((p) => p[0]), ys = simple.map((p) => p[1]);
  const origin: Pt = [Math.floor(Math.min(...xs)), Math.floor(Math.min(...ys))];
  const ring = simple.map(([x, y]) => [x - origin[0], y - origin[1]] as Pt);
  if (selfIntersects(ring)) warnings.push("Conturul se autointersectează. Verifică ordinea punctelor.");
  const area = Math.abs(ringArea(ring));
  if (area < 50) warnings.push(`Suprafața de ${area.toFixed(1)} m² e prea mică pentru o parcelă. Unitățile sunt metri?`);
  return { ring, origin, area, removed: raw.length - simple.length, warnings };
}

/** Outward normal of edge i of a counter-clockwise ring. */
function outward(r: Ring, i: number): Pt {
  const a = r[i], b = r[(i + 1) % r.length];
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  return [(b[1] - a[1]) / len, -(b[0] - a[0]) / len];
}

export const edgeLength = (r: Ring, i: number) => {
  const a = r[i], b = r[(i + 1) % r.length];
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
};

/**
 * Edge kinds from the street edges: an edge facing away from the street is
 * posterior, anything else lateral. Only a suggestion: which side is the
 * street is a fact the user knows and the geometry does not.
 */
export function suggestEdges(r: Ring, street: number[]): EdgeKind[] {
  const s = street.map((i) => outward(r, i));
  const facing: Pt = [s.reduce((t, n) => t + n[0], 0), s.reduce((t, n) => t + n[1], 0)];
  const len = Math.hypot(...facing) || 1;
  return r.map((_, i) => {
    if (street.includes(i)) return "aliniament";
    const n = outward(r, i);
    return (n[0] * facing[0] + n[1] * facing[1]) / len < -0.6 ? "posterior" : "lateral";
  });
}

/** The longest edge: the usual first guess for the street frontage. */
export function longestEdge(r: Ring): number {
  let best = 0;
  r.forEach((_, i) => { if (edgeLength(r, i) > edgeLength(r, best)) best = i; });
  return best;
}

export function siteFrom(p: PreparedPlot, kinds: EdgeKind[]): Site {
  return { boundary: p.ring, edges: kinds };
}
